import { createServerFn } from '@tanstack/react-start';
import { defineQuery } from 'groq';

import type {
  DirectoryTownQueryResult,
  DirectoryTownsQueryResult,
} from '@/sanity.types';

import { createSanityClient } from './client';
import { landingSectionProjection } from './landing-page';

export type DirectoryTown = NonNullable<DirectoryTownQueryResult>;
export type DirectoryTownSummary = DirectoryTownsQueryResult[number];

const directoryTownProjection = `
  "id": _id,
  "updatedAt": _updatedAt,
  name,
  "slug": slug.current,
  region,
  directoryAreaSlug,
  seoTitle,
  seoDescription,
  intro,
  "sections": coalesce(sections[] {
    ${landingSectionProjection}
  }, []),
  "feeGuide": feeGuide-> {
    title,
    "slug": slug.current,
    excerpt
  }
`;

const directoryTownsQuery = defineQuery(`*[
  _type == "town" &&
  defined(slug.current)
] | order(name asc) {
  "id": _id,
  name,
  "slug": slug.current,
  region,
  directoryAreaSlug,
  intro
}`);

const directoryTownQuery = defineQuery(`*[
  _type == "town" &&
  slug.current == $slug
][0] {
  ${directoryTownProjection}
}`);

const directoryTownByAreaQuery = defineQuery(`*[
  _type == "town" &&
  directoryAreaSlug == $areaSlug
][0] {
  ${directoryTownProjection}
}`);

const sitemapDirectoryTownsQuery = defineQuery(`*[
  _type == "town" &&
  defined(slug.current)
] | order(slug.current asc) {
  "slug": slug.current,
  "updatedAt": _updatedAt
}`);

/** Public server function: it only reads published town summaries from Sanity. */
export const getDirectoryTowns = createServerFn({ method: 'GET' }).handler(
  async (): Promise<DirectoryTownsQueryResult> => {
    const client = createSanityClient();

    try {
      return await client.fetch(directoryTownsQuery);
    } catch (error) {
      console.error('Failed to load Sanity directory towns', error);
      return [];
    }
  },
);

/** Public server function: it only reads one published town from Sanity. */
export const getDirectoryTown = createServerFn({ method: 'GET' })
  .validator((slug: unknown) => {
    if (typeof slug !== 'string') {
      throw new Error('Town slug must be a string');
    }

    const normalizedSlug = slug.trim();
    if (normalizedSlug.length === 0 || normalizedSlug.length > 96) {
      throw new Error('Town slug must be between 1 and 96 characters');
    }

    return normalizedSlug;
  })
  .handler(async ({ data: slug }): Promise<DirectoryTown | null> => {
    const client = createSanityClient();

    try {
      return await client.fetch(directoryTownQuery, { slug });
    } catch (error) {
      console.error('Failed to load Sanity directory town', error);
      return null;
    }
  });

/** Public server function: it reads the town whose directory area matches this slug. */
export const getDirectoryTownByAreaSlug = createServerFn({ method: 'GET' })
  .validator((areaSlug: unknown) => {
    if (typeof areaSlug !== 'string') {
      throw new Error('Directory area slug must be a string');
    }

    const normalizedSlug = areaSlug.trim();
    if (normalizedSlug.length === 0 || normalizedSlug.length > 96) {
      throw new Error(
        'Directory area slug must be between 1 and 96 characters',
      );
    }

    return normalizedSlug;
  })
  .handler(async ({ data: areaSlug }): Promise<DirectoryTown | null> => {
    const client = createSanityClient();

    try {
      return await client.fetch(directoryTownByAreaQuery, { areaSlug });
    } catch (error) {
      console.error('Failed to load Sanity directory town for area', error);
      return null;
    }
  });

type SitemapDirectoryTown = {
  slug: string;
  updatedAt: string | null;
};

const isSitemapDirectoryTown = (
  town: unknown,
): town is SitemapDirectoryTown => {
  if (typeof town !== 'object' || town === null) {
    return false;
  }

  if (!('slug' in town) || typeof town.slug !== 'string') {
    return false;
  }

  if (!('updatedAt' in town)) {
    return false;
  }

  return town.updatedAt == null || typeof town.updatedAt === 'string';
};

export async function getSitemapDirectoryTowns(): Promise<
  SitemapDirectoryTown[]
> {
  const client = createSanityClient();

  try {
    const towns = await client.fetch(sitemapDirectoryTownsQuery);

    return Array.isArray(towns) ? towns.filter(isSitemapDirectoryTown) : [];
  } catch (error) {
    console.error('Failed to load Sanity directory towns for sitemap', error);
    return [];
  }
}
